// Modale de gestion d'un groupe existant.
// Créateur : peut renommer + ajouter/retirer des membres + supprimer le groupe (via retrait
// de tous les membres — le back nettoie automatiquement quand il ne reste plus qu'une personne).
// Non-créateur : peut voir la liste des membres + quitter le groupe.
import React, { useEffect, useState } from "react";
import {
	getGroupInfo,
	addGroupMember,
	removeGroupMember,
	leaveGroup,
	renameGroup,
	getAllPartners,
} from "../services/authService";

export default function GroupSettingsModal({ idGroup, currentUserId, onClose, onGroupUpdated, onGroupLeft }) {
	const [info, setInfo] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");

	const [renameMode, setRenameMode] = useState(false);
	const [newName, setNewName] = useState("");
	const [saving, setSaving] = useState(false);

	const [addingMember, setAddingMember] = useState(false);
	const [availablePartners, setAvailablePartners] = useState([]);

	const load = async () => {
		setLoading(true);
		const data = await getGroupInfo(idGroup);
		setLoading(false);
		if (data) {
			setInfo(data);
			setNewName(data.nom || "");
		} else {
			setError("Impossible de charger le groupe.");
		}
	};

	useEffect(() => {
		load();
	}, [idGroup]);

	const isCreator = info?.isCurrentUserCreator === true;

	const memberIdSet = new Set((info?.members || []).map((m) => m.idUser));

	const handleRename = async () => {
		if (!newName.trim() || newName === info.nom) {
			setRenameMode(false);
			return;
		}
		setSaving(true);
		const ok = await renameGroup(idGroup, newName.trim());
		setSaving(false);
		if (ok) {
			setInfo({ ...info, nom: newName.trim() });
			setRenameMode(false);
			onGroupUpdated?.();
		} else {
			setError("Renommage échoué.");
		}
	};

	const handleRemoveMember = async (idUser, prenom) => {
		if (!window.confirm(`Retirer ${prenom} du groupe ?`)) return;
		setSaving(true);
		const ok = await removeGroupMember(idGroup, idUser);
		setSaving(false);
		if (ok) {
			await load();
			onGroupUpdated?.();
		} else {
			setError("Retrait échoué.");
		}
	};

	const handleLeave = async () => {
		if (!window.confirm("Quitter ce groupe ?")) return;
		setSaving(true);
		const ok = await leaveGroup(idGroup);
		setSaving(false);
		if (ok) {
			onGroupLeft?.();
		} else {
			setError("Sortie du groupe échouée.");
		}
	};

	const openAddMember = async () => {
		setError("");
		const result = await getAllPartners();
		if (result.success) {
			setAvailablePartners(result.partners.filter((p) => !memberIdSet.has(p.id)));
			setAddingMember(true);
		} else {
			setError(result.error || "Erreur");
		}
	};

	const handleAddMember = async (partner) => {
		setSaving(true);
		const ok = await addGroupMember(idGroup, partner.id);
		setSaving(false);
		if (ok) {
			setAddingMember(false);
			await load();
			onGroupUpdated?.();
		} else {
			setError("Ajout échoué.");
		}
	};

	return (
		<div className="mens-modal-overlay" onClick={onClose}>
			<div className="mens-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 460 }}>
				<div className="mens-modal-header">
					<h3>Paramètres du groupe</h3>
					<button className="mens-modal-close" onClick={onClose} aria-label="Fermer">×</button>
				</div>

				{loading ? (
					<div className="mens-loading">Chargement...</div>
				) : !info ? (
					<div className="mens-error">{error || "Groupe introuvable."}</div>
				) : (
					<>
						{/* Nom + renommage */}
						<div className="group-settings-section">
							<span className="group-settings-label">Nom du groupe</span>
							{renameMode ? (
								<div className="group-settings-rename-row">
									<input
										className="mens-modal-date"
										value={newName}
										onChange={(e) => setNewName(e.target.value)}
										maxLength={100}
										autoFocus
										style={{ flex: 1 }}
									/>
									<button className="mens-btn primary small" onClick={handleRename} disabled={saving}>
										{saving ? "..." : "OK"}
									</button>
									<button className="mens-btn ghost small" onClick={() => { setRenameMode(false); setNewName(info.nom); }}>Annuler</button>
								</div>
							) : (
								<div className="group-settings-rename-row">
									<span className="group-settings-name">{info.nom}</span>
									{isCreator && (
										<button className="mens-btn ghost small" onClick={() => setRenameMode(true)}>Renommer</button>
									)}
								</div>
							)}
						</div>

						{/* Membres */}
						<div className="group-settings-section">
							<div className="group-settings-header-row">
								<span className="group-settings-label">Membres ({(info.members || []).length})</span>
								{isCreator && (
									<button className="mens-btn ghost small" onClick={openAddMember} disabled={saving}>
										+ Ajouter
									</button>
								)}
							</div>
							<ul className="group-settings-members">
								{(info.members || []).map((m) => (
									<li key={m.idUser} className="group-settings-member">
										<span className="group-settings-member-name">
											{m.prenom} {m.nom}
											{m.isCreator && <span className="group-settings-creator-badge">Créateur</span>}
										</span>
										{isCreator && !m.isCreator && (
											<button
												className="mens-btn danger small"
												onClick={() => handleRemoveMember(m.idUser, m.prenom)}
												disabled={saving}
											>
												Retirer
											</button>
										)}
									</li>
								))}
							</ul>
						</div>

						{error && <div className="mens-modal-error">{error}</div>}

						{/* Actions */}
						<div className="mens-modal-actions" style={{ justifyContent: "space-between" }}>
							{!isCreator ? (
								<button className="mens-btn danger" onClick={handleLeave} disabled={saving}>
									Quitter le groupe
								</button>
							) : <span />}
							<button className="mens-btn ghost" onClick={onClose}>Fermer</button>
						</div>

						{/* Overlay d'ajout membre */}
						{addingMember && (
							<div className="mens-modal-overlay" onClick={() => setAddingMember(false)}>
								<div className="mens-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 400 }}>
									<div className="mens-modal-header">
										<h3>Ajouter un membre</h3>
										<button className="mens-modal-close" onClick={() => setAddingMember(false)}>×</button>
									</div>
									{availablePartners.length === 0 ? (
										<p style={{ color: "var(--ink-soft)", margin: 0, padding: "12px 0" }}>
											Tous tes partenaires sont déjà dans ce groupe.
										</p>
									) : (
										<ul className="group-settings-members">
											{availablePartners.map((p) => (
												<li key={p.id} className="group-settings-member">
													<span className="group-settings-member-name">{p.prenom} {p.nom}</span>
													<button
														className="mens-btn primary small"
														onClick={() => handleAddMember(p)}
														disabled={saving}
													>
														Ajouter
													</button>
												</li>
											))}
										</ul>
									)}
								</div>
							</div>
						)}
					</>
				)}
			</div>
		</div>
	);
}
